import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { ClassificationType } from "@/types/entity";

type EffectiveClassification = ClassificationType | "UNCLASSIFIED";

type ClassificationBadgeProps = {
  classification?: ClassificationType | null;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "dark";
  className?: string;
};

const DEFAULT_LABELS: Record<EffectiveClassification, string> = {
  ESSENTIAL: "Entitate Esențială",
  IMPORTANT: "Entitate Importantă",
  OUT_OF_SCOPE: "În afara domeniului",
  IMPACT: "Necesită Analiză Efect Perturbator",
  UNCLASSIFIED: "Neclasificată",
};

const SHORT_LABELS: Record<EffectiveClassification, string> = {
  ESSENTIAL: "Esențială",
  IMPORTANT: "Importantă",
  OUT_OF_SCOPE: "În afara domeniului",
  IMPACT: "Analiză Ef Perturbator",
  UNCLASSIFIED: "Neclasificată",
};

const COLOR_CLASSES: Record<
  "default" | "dark",
  Record<EffectiveClassification, string>
> = {
  default: {
    ESSENTIAL: "bg-ds-blue-600 text-black hover:bg-ds-blue-400",
    IMPORTANT: "bg-ds-green-500 text-black hover:bg-ds-green-500",
    OUT_OF_SCOPE: "bg-ds-text-500 text-black hover:bg-ds-text-500",
    IMPACT: "bg-ds-amber-500 text-black hover:bg-ds-amber-500",
    UNCLASSIFIED: "bg-ds-text-400 text-navy-800 hover:bg-ds-text-400",
  },
  dark: {
    ESSENTIAL: "bg-ds-blue-800/50 text-ds-blue-300 hover:bg-ds-blue-800/50",
    IMPORTANT: "bg-ds-green-600/50 text-ds-green-100 hover:bg-ds-green-600/50",
    OUT_OF_SCOPE: "bg-ds-border-700 text-ds-text-400 hover:bg-ds-border-700",
    IMPACT: "bg-ds-amber-500/50 text-ds-amber-100 hover:bg-ds-amber-500/50",
    UNCLASSIFIED: "bg-ds-text-600 text-ds-text-300 hover:bg-ds-text-600",
  },
};

const DASH_CLASSES: Record<"default" | "dark", string> = {
  default: "bg-ds-border-200 text-ds-text-500",
  dark: "bg-ds-border-700 text-ds-text-400",
};

const SIZE_CLASSES: Record<"sm" | "md" | "lg", string> = {
  sm: "text-xs px-2 py-0.5",
  md: "text-sm px-2 py-2",
  lg: "text-lg px-4 py-2",
};

export function ClassificationBadge({
  classification,
  size = "md",
  variant = "default",
  className,
}: ClassificationBadgeProps) {
  const sizeClass = SIZE_CLASSES[size];

  if (classification === null) {
    return (
      <Badge className={cn(DASH_CLASSES[variant], sizeClass, className)}>
        -
      </Badge>
    );
  }

  const effectiveClassification: EffectiveClassification =
    classification ?? "UNCLASSIFIED";
  const labels = size === "sm" ? SHORT_LABELS : DEFAULT_LABELS;
  const displayLabel = labels[effectiveClassification];
  const colorClass = COLOR_CLASSES[variant][effectiveClassification];

  return (
    <Badge className={cn(colorClass, sizeClass, className, "rounded-sm")}>
      {displayLabel.toUpperCase()}
    </Badge>
  );
}
